import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useTiktokPageShopScope, stackShopDimensionTabs } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokMarketingActivityDrawer from '@/tiktok-platform/modules/ops/TiktokMarketingActivityDrawer';
import MarketingEnrollSessionsModal from '@/tiktok-platform/modules/ops/MarketingEnrollSessionsModal';
import HoverCopyField from '@/tiktok-platform/modules/products/components/HoverCopyField';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { buildTiktokPath, openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import {
  TIKTOK_ACTIVITY_STATUS_TABS,
  TIKTOK_ACTIVITY_TYPE_TABS,
  tiktokActivityStatusLabel,
  tiktokActivityStatusTone,
  tiktokActivityTypeLabel,
} from '@/tiktok-platform/utils/tiktokMarketingDisplay';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

/** @deprecated Temu 数字类型；TTS 用字符串枚举，保留兼容旧缓存行 */
const LEGACY_ACTIVITY_TYPE_LABEL_BY_CODE = {
  1: '限时秒杀',
  5: '大促活动',
  13: '活动专题',
  14: '活动专题',
  21: '超级秒杀',
  27: '清仓甩卖',
  31: '超级万人团',
  101: '活动专题',
  127: '活动专题',
};

function activityTypeDisplayLabel(row) {
  const code = row?.activityType != null ? String(row.activityType).trim() : '';
  if (code) {
    const tts = tiktokActivityTypeLabel(code);
    if (tts !== '—' && tts !== code) return tts;
    if (LEGACY_ACTIVITY_TYPE_LABEL_BY_CODE[code]) {
      return fixedUi(i18n.t('marketing.tmt' + code, { ns: 'workbench', defaultValue: LEGACY_ACTIVITY_TYPE_LABEL_BY_CODE[code] }));
    }
  }
  const raw = row?.activityTypeLabel || row?.type || code;
  if (!raw) return '—';
  return tiktokActivityTypeLabel(raw) !== '—' && tiktokActivityTypeLabel(raw) !== String(raw)
    ? tiktokActivityTypeLabel(raw)
    : (LEGACY_ACTIVITY_TYPE_LABEL_BY_CODE[String(raw).trim()]
      ? fixedUi(i18n.t('marketing.tmt' + String(raw).trim(), { ns: 'workbench', defaultValue: LEGACY_ACTIVITY_TYPE_LABEL_BY_CODE[String(raw).trim()] }))
      : fixedUi(raw));
}

function activityStatusDisplayLabel(row) {
  const fromCode = tiktokActivityStatusLabel(row?.status || row?.activityStatus);
  if (fromCode !== '—') return fromCode;
  const fromLabel = tiktokActivityStatusLabel(row?.statusLabel || row?.activityStatusLabel);
  return fromLabel !== '—' ? fromLabel : '—';
}

const EMPTY_FILTER_DRAFT = {
  keyword: '',
  status: '',
};

const EMPTY_APPLIED = {
  keyword: '',
  activityType: '',
  status: '',
};

function dayStartEpochSeconds(dateStr) {
  if (!dateStr) return undefined;
  const ms = Date.parse(`${dateStr}T00:00:00+08:00`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

function dayEndEpochSeconds(dateStr) {
  if (!dateStr) return undefined;
  const ms = Date.parse(`${dateStr}T23:59:59+08:00`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

function epochToDateInput(epoch) {
  const n = Number(epoch);
  if (!Number.isFinite(n) || n <= 0) return '';
  const date = new Date(n * 1000);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const VIEW_META = {
  activities: {
    title: '活动列表',
    titleKey: 'marketing.actList',
    syncKinds: ['marketing'],
    syncKind: 'marketing',
    quotaKind: 'marketing',
    dumpKind: 'marketing',
    colSpan: 7,
    syncHint: '从 TikTok 后台拉取促销活动到本地缓存；进页读取本地数据，点击同步更新。',
    syncHintKey: 'marketing.ttSyncAct',
    emptyNever: '尚未同步促销活动',
    emptyNeverKey: 'marketing.ttNeverAct',
    emptyNone: '暂无促销活动',
    emptyNoneKey: 'marketing.ttNoneAct',
    loadingLabel: '正在查询活动…',
    loadingLabelKey: 'marketing.queryAct',
    keywordPlaceholder: '活动标题（精确关键词）',
    keywordPlaceholderKey: 'marketing.ttKwAct',
    selectAllLabelKey: 'marketing.selectAct',
    pagerLabelKey: 'marketing.pagerAct',
    exportFilePrefix: 'tiktok-marketing-activities',
    selectAllLabel: '全选活动',
    pagerLabel: '活动列表分页',
  },
  enrolls: {
    title: '活动商品',
    titleKey: 'marketing.goodsList',
    syncKinds: ['marketingProduct'],
    syncKind: 'marketingProduct',
    quotaKind: 'marketing',
    dumpKind: 'marketing-product',
    colSpan: 9,
    syncHint: '同步时一并拉取活动商品到本地缓存；进页读取本地数据。',
    syncHintKey: 'marketing.ttSyncGoods',
    emptyNever: '尚未同步活动商品',
    emptyNeverKey: 'marketing.ttNeverGoods',
    emptyNone: '暂无活动商品，请先同步或到活动详情查看',
    emptyNoneKey: 'marketing.ttNoneGoods',
    loadingLabel: '正在查询…',
    loadingLabelKey: 'marketing.queryShort',
    keywordPlaceholder: '（本页暂不支持）',
    keywordPlaceholderKey: 'marketing.kwUnsupported',
    selectAllLabelKey: 'marketing.selectAll',
    pagerLabelKey: 'marketing.pagerGoods',
    exportFilePrefix: 'tiktok-marketing-enrolls',
    selectAllLabel: '全选',
    pagerLabel: '活动商品分页',
  },
};

function extractListPayload(res) {
  if (!res || typeof res !== 'object') {
    return {
      items: [],
      total: 0,
      lastSyncedAt: null,
      neverSynced: true,
      typeCounts: { all: 0 },
      statusCounts: { all: 0 },
    };
  }
  const items = Array.isArray(res.items)
    ? res.items
    : (Array.isArray(res.content) ? res.content : (Array.isArray(res.data) ? res.data : []));
  const total = Number.isFinite(Number(res.totalElements))
    ? Number(res.totalElements)
    : (Number.isFinite(Number(res.total)) ? Number(res.total) : items.length);
  const lastSyncedAt = res.lastSyncedAt || res.syncedAt || null;
  const neverSynced = Boolean(res.neverSynced) || (!lastSyncedAt && items.length === 0);
  const typeCounts = res.typeCounts && typeof res.typeCounts === 'object'
    ? res.typeCounts
    : { all: total };
  const statusCounts = res.statusCounts && typeof res.statusCounts === 'object'
    ? res.statusCounts
    : { all: total };
  return { items, total, lastSyncedAt, neverSynced, typeCounts, statusCounts };
}

function rowKey(row, view, index = 0) {
  if (view === 'enrolls') {
    return String(row?.id || row?.enrollId || `enroll-${index}`);
  }
  return String(row?.id || row?.activityThematicId || row?.activityId || `activity-${index}`);
}

function formatMoney(value, currency) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  if (!raw) return '—';
  if (raw.startsWith('$') || raw.startsWith('¥') || raw.startsWith('€')) return raw;
  const prefix = currency === 'CNY' || currency === 'RMB' ? '¥' : (currency === 'USD' ? '$' : (currency ? `${currency} ` : '$'));
  return `${prefix}${raw}`;
}

/** 活动价 / 供货价 → 折扣（如 300/346 ≈ 8.7折） */
function parsePriceNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatActivityDiscount(activityPrice, supplyPrice) {
  const activity = parsePriceNumber(activityPrice);
  const supply = parsePriceNumber(supplyPrice);
  if (activity == null || supply == null) return null;
  // 无减价：按原价展示；有减价时不得四舍五入成「10折」
  if (activity >= supply) return fixedUi('原价');
  const zhe = (activity / supply) * 10;
  if (!Number.isFinite(zhe) || zhe <= 0) return null;
  // 接近 10 折时保留两位，避免 326/327 被收成 10 折
  const text = zhe >= 9.95 ? zhe.toFixed(2) : (Math.round(zhe * 10) / 10).toFixed(1);
  return fixedUiTpl('{0}折', text);
}

function enrollSkcItems(row) {
  if (Array.isArray(row?.skcItems) && row.skcItems.length) return row.skcItems;
  return [{
    colorLabel: row?.skcSummary || row?.sku || null,
    skcName: null,
    supplierCode: null,
    imageUrl: row?.productImageUrl || null,
    supplyPrice: null,
    activityPrice: null,
  }];
}

/** 产品级货号：优先行字段，否则取首个有值的 SKC 货号（对齐产品信息页） */
function enrollProductSupplierCode(row, skcItems) {
  const fromRow = String(row?.supplierCode || row?.extCode || '').trim();
  if (fromRow) return fromRow;
  for (const skc of skcItems || []) {
    const code = String(skc?.supplierCode || '').trim();
    if (code) return code;
  }
  return '';
}

function activityStatusTone(labelOrCode) {
  return tiktokActivityStatusTone(labelOrCode);
}

function enrollStatusTone(labelOrCode) {
  return tiktokActivityStatusTone(labelOrCode);
}

function soldStatusTone(code) {
  const n = Number(code);
  if (n === 1) return 'warn';
  if (n === 2) return 'danger';
  return 'ok';
}

function toExportRows(view, source) {
  if (view === 'enrolls') {
    return source.map((row) => ({
      productName: row.productName || '',
      productId: row.productId ?? '',
      skcSummary: row.skcSummary || row.sku || '',
      supplyPrice: (enrollSkcItems(row).map((s) => s.supplyPrice).filter(Boolean).join(' / ')) || '',
      activityPrice: (enrollSkcItems(row).map((s) => s.activityPrice).filter(Boolean).join(' / ')) || '',
      enrollTime: row.enrollTime || '',
      activityThematicName: row.activityThematicName || row.name || '',
      sessionStartTime: row.sessionStartTime || row.startTime || '',
      sessionEndTime: row.sessionEndTime || row.endTime || '',
      activityStatus: row.activityStatusLabel || row.sessionStatusLabel || '',
      enrollStatus: row.enrollStatusLabel || '',
      activityStock: row.activityStock ?? '',
      remainingActivityStock: row.remainingActivityStock ?? '',
      soldStatus: row.soldStatusLabel || '',
    }));
  }
  return source.map((row) => ({
    name: row.name || row.activityName || '',
    activityThematicId: row.activityThematicId || row.activityId || '',
      activityType: activityTypeDisplayLabel(row),
      status: activityStatusDisplayLabel(row),
    enrollDeadline: row.enrollDeadline || '',
    startTime: row.startTime || '',
    endTime: row.endTime || '',
    
  }));
}

function exportColumns(view) {
  if (view === 'enrolls') {
    return [
      'productName',
      'productId',
      'skcSummary',
      'supplyPrice',
      'activityPrice',
      'enrollTime',
      'activityThematicName',
      'sessionStartTime',
      'sessionEndTime',
      'activityStatus',
      'enrollStatus',
      'activityStock',
      'remainingActivityStock',
      'soldStatus',
    ];
  }
  return [
    'name',
    'activityThematicId',
    'activityType',
    'status',
    'enrollDeadline',
    'startTime',
    'endTime',
  ];
}

/**
 * 营销活动子页：activities | enrolls
 */
export default function TiktokMarketingPage({ view = 'activities' }) {
  const { t } = useTranslation('workbench');
  const pageView = VIEW_META[view] ? view : 'activities';
  const meta = VIEW_META[pageView];
  const { shopDimensionTabs, concreteScopeId } = useTiktokPageShopScope();
  const shopId = concreteScopeId || '';

  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTER_DRAFT });
  const [applied, setApplied] = useState({ ...EMPTY_APPLIED });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({ all: 0 });
  const [statusCounts, setStatusCounts] = useState({ all: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [neverSynced, setNeverSynced] = useState(true);
  const [error, setError] = useState('');
  const [drawerActivity, setDrawerActivity] = useState(null);
  const [drawerTab, setDrawerTab] = useState('detail');
  const [sessionsModal, setSessionsModal] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    activity_type: 'DIRECT_DISCOUNT',
    begin_time: '',
    end_time: '',
  });
  const [editActivity, setEditActivity] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', begin_time: '', end_time: '' });
  const [editBusy, setEditBusy] = useState(false);
  const [republishActivity, setRepublishActivity] = useState(null);
  const [republishForm, setRepublishForm] = useState({ begin_time: '', end_time: '' });
  const [republishBusy, setRepublishBusy] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, pageView, index)).filter(Boolean),
    [rows, pageView],
  );

  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = useCallback(() => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  }, [selectableKeys, allSelected]);

  const toggleOne = useCallback((key, row) => {
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    rows.forEach((row, index) => {
      const key = rowKey(row, pageView, index);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys, pageView]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [shopId, pageView, applied]);

  useEffect(() => {
    setPage(1);
  }, [shopId, pageView]);

  const resolveSelectedRows = useCallback(() => (
    Array.from(selectedKeys)
      .map((key) => selectedRowsRef.current.get(key))
      .filter(Boolean)
  ), [selectedKeys]);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, []);

  const exportRows = useCallback((source) => {
    if (!source.length) {
      showToast(fixedUi("暂无可导出数据"), 'warning');
      return;
    }
    downloadCsv(
      `${meta.exportFilePrefix}-${Date.now()}.csv`,
      exportColumns(pageView),
      toExportRows(pageView, source),
    );
    showToast(fixedUiTpl('已导出 {0} 条', source.length), 'success');
  }, [meta.exportFilePrefix, pageView]);

  const exportFiltered = useCallback(async () => {
    if (!shopId) {
      showToast(t('marketing.pickTiktok'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const filters = applied;
      const currentView = pageView;
      const all = await fetchAllPages(async (pageNo, size) => {
        const params = {
          page: Math.max(0, pageNo - 1),
          size,
          keyword: filters.keyword || undefined,
          activityType: filters.activityType || undefined,
        };
        let res;
        if (currentView === 'enrolls') {
          res = await tiktokPlatformAPI.listMarketingEnrolls(shopId, {
            ...params,
            sessionStatus: filters.sessionStatus === '' ? undefined : Number(filters.sessionStatus),
            enrollStatus: filters.enrollStatus === '' ? undefined : Number(filters.enrollStatus),
            enrollTimeBegin: dayStartEpochSeconds(filters.enrollTimeBegin),
            enrollTimeEnd: dayEndEpochSeconds(filters.enrollTimeEnd),
          });
        } else {
          res = await tiktokPlatformAPI.listMarketing(shopId, {
            ...params,
            status: filters.status || undefined,
          });
        }
        const payload = extractListPayload(res);
        return { items: payload.items, total: payload.total };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, applied, pageView, exportRows, t]);

  const copySelectedIds = useCallback(async () => {
    const selected = resolveSelectedRows();
    if (!selected.length) {
      showToast(fixedUi("请先勾选数据"), 'warning');
      return;
    }
    const ids = selected.map((row, index) => {
      if (pageView === 'enrolls') {
        return String(row.productId || row.enrollId || row.id || rowKey(row, pageView, index));
      }
      return String(row.activityThematicId || row.activityId || row.id || rowKey(row, pageView, index));
    }).filter(Boolean);
    const unique = [...new Set(ids)];
    const text = unique.join('\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast(fixedUiTpl('已复制 {0} 个 ID', unique.length), 'success');
    } catch {
      showToast(fixedUi("复制失败"), 'error');
    }
  }, [pageView, resolveSelectedRows]);

  const deactivateSelected = useCallback(async () => {
    const selected = resolveSelectedRows();
    if (!shopId || selected.length === 0) {
      showToast(fixedUi("请先选中活动"), 'warning');
      return;
    }
    try {
      for (const row of selected) {
        if (row.immutable) continue;
        const activityId = row.activityId || row.activityThematicId || row.id;
        if (!activityId) continue;
        await tiktokPlatformAPI.deactivatePromotionActivity(shopId, activityId);
      }
      showToast(t('marketing.deactivated'), 'success');
    } catch (e) {
      showToast(e?.message || t('marketing.deactivateFail'), 'error');
    }
  }, [resolveSelectedRows, shopId, t]);

  const batchViewActivity = useCallback(() => {
    const selected = resolveSelectedRows();
    if (selected.length !== 1) {
      showToast(t('marketing.oneActivity'), 'warning');
      return;
    }
    setDrawerActivity(selected[0]);
  }, [resolveSelectedRows, t]);

  const batchOpsMenu = useMemo(() => {
    const empty = selectedKeys.size === 0;
    const copyLabel = pageView === 'activities' ? t('marketing.copyThematic') : t('marketing.copyGoods');
    const items = [
      {
        key: 'copy-ids',
        label: copyLabel,
        disabled: empty,
        onClick: copySelectedIds,
      },
      {
        key: 'export-selected-batch',
        label: t('marketing.exportSelected'),
        disabled: empty,
        onClick: () => exportRows(resolveSelectedRows()),
      },
    ];
    if (pageView === 'activities') {
      items.unshift(
        {
          key: 'create-activity',
          label: t('marketing.createAct'),
          disabled: !shopId,
          onClick: () => setCreateOpen(true),
        },
        {
          key: 'deactivate-activity',
          label: t('marketing.deactivateSel'),
          disabled: empty,
          onClick: deactivateSelected,
        },
        {
          key: 'batch-view',
          label: t('marketing.viewSel'),
          disabled: selectedKeys.size !== 1,
          onClick: batchViewActivity,
        },
      );
    }
    items.push({
      key: 'clear-selection',
      label: t('marketing.clearSel'),
      disabled: empty,
      onClick: clearSelection,
    });
    return items;
  }, [
    selectedKeys.size,
    pageView,
    copySelectedIds,
    exportRows,
    resolveSelectedRows,
    batchViewActivity,
    clearSelection,
    shopId,
    deactivateSelected,
    t,
  ]);

  const onRowClickSelect = useCallback((event, key, row) => {
    if (event.target.closest('button, a, input, label, .tko-marketing-list__ops')) return;
    toggleOne(key, row);
  }, [toggleOne]);

  const loadList = useCallback(async (sid, filters, currentView, pageNo, size, options = {}) => {
    if (!sid) {
      setRows([]);
      setTotal(0);
      setTypeCounts({ all: 0 });
      setStatusCounts({ all: 0 });
      setNeverSynced(true);
      setLastSyncedAt(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {
        page: Math.max(0, (Number(pageNo) || 1) - 1),
        size: Math.max(1, Number(size) || TIKTOK_OPS_DEFAULT_PAGE_SIZE),
        keyword: filters.keyword || undefined,
        activityType: filters.activityType || undefined,
      };
      let res;
      if (currentView === 'enrolls') {
        res = await tiktokPlatformAPI.listMarketingEnrolls(sid, {
          ...params,
          sessionStatus: filters.sessionStatus === '' ? undefined : Number(filters.sessionStatus),
          enrollStatus: filters.enrollStatus === '' ? undefined : Number(filters.enrollStatus),
          enrollTimeBegin: dayStartEpochSeconds(filters.enrollTimeBegin),
          enrollTimeEnd: dayEndEpochSeconds(filters.enrollTimeEnd),
        });
      } else {
        res = await tiktokPlatformAPI.listMarketing(sid, {
          ...params,
          status: filters.status || undefined,
        });
      }
      const payload = extractListPayload(res);
      setRows(payload.items);
      setTotal(payload.total);
      setTypeCounts(payload.typeCounts);
      setStatusCounts(payload.statusCounts);
      setLastSyncedAt(payload.lastSyncedAt || (options.fromSync ? new Date().toISOString() : null));
      setNeverSynced(options.fromSync ? false : payload.neverSynced);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setTypeCounts({ all: 0 });
      setStatusCounts({ all: 0 });
      setError(e?.message || t('marketing.loadFail', { title: t(meta.titleKey) }));
      showToast(e?.message || t('marketing.loadFail', { title: t(meta.titleKey) }), 'error');
    } finally {
      setLoading(false);
    }
  }, [meta.titleKey, t]);

  useEffect(() => {
    setFilterDraft({ ...EMPTY_FILTER_DRAFT });
    setApplied({ ...EMPTY_APPLIED });
    setPage(1);
    setRows([]);
    setTotal(0);
    setTypeCounts({ all: 0 });
    setStatusCounts({ all: 0 });
    setError('');
    setLastSyncedAt(null);
    setNeverSynced(true);
    setSelectedKeys(new Set());
    selectedRowsRef.current = new Map();
  }, [shopId, pageView]);

  // 进页读本地缓存；筛选/分页直接请求本地列表
  useEffect(() => {
    if (!shopId) return undefined;
    loadList(shopId, applied, pageView, page, pageSize);
    return undefined;
  }, [shopId, applied, pageView, page, pageSize, loadList]);

  const onQuery = () => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      keyword: String(filterDraft.keyword || '').trim(),
      status: String(filterDraft.status || ''),
    }));
  };

  const onReset = () => {
    setFilterDraft({ ...EMPTY_FILTER_DRAFT });
    setPage(1);
    setApplied({ ...EMPTY_APPLIED });
  };

  const onActivityTypeTab = (value) => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      activityType: String(value || ''),
    }));
  };

  const onStatusTab = (value) => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      status: String(value || ''),
    }));
  };

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('marketing.pickTiktok'), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: meta.syncKind,
        shopId,
        label: t('publishRecord.syncUpdate'),
        payload: { syncProducts: true },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      await loadList(shopId, applied, pageView, page, pageSize, { fromSync: true });
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      if (e?.message) showToast(e.message, 'error');
    }
  }, [shopId, applied, pageView, page, pageSize, loadList, startJob, onTrackDone, meta.syncKind, t]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync, { minIntervalMs: 5000 });

  const syncing = syncingLocal || isBusy(meta.syncKind, shopId);

  const lastSyncedLabel = useMemo(() => {
    if (syncing || loading) return t('productAction.syncing');
    if (!lastSyncedAt) return t('marketing.neverPullTt');
    try {
      return t('marketing.syncedAt', { time: formatSiteListSyncedAt(lastSyncedAt) });
    } catch {
      return t('marketing.syncedAt', { time: String(lastSyncedAt) });
    }
  }, [syncing, loading, lastSyncedAt, t]);

  const emptyTitle = useMemo(() => {
    if (!shopId) return t('returnList.pickShop');
    if (loading) return t('returnList.loading');
    if (neverSynced && rows.length === 0) return t(meta.emptyNeverKey);
    if (rows.length === 0) return t(meta.emptyNoneKey);
    return t('freightLib.noData');
  }, [shopId, loading, neverSynced, rows.length, meta, t]);

  const emptyHint = useMemo(() => {
    if (!shopId) return '';
    if (neverSynced && rows.length === 0 && !loading) {
      if (pageView === 'enrolls') {
        return t('marketing.pullEnrollTt');
      }
      return t('marketing.pullActTt');
    }
    const hasFilter = Boolean(
      applied.keyword
      || applied.activityType
      || applied.status
    );
    if (rows.length === 0 && !loading && hasFilter) {
      return t('marketing.adjustFilter');
    }
    return '';
  }, [shopId, neverSynced, rows.length, loading, applied, pageView, t]);

  const typeTabs = useMemo(() => (
    <div className="tko-status-tabs" role="tablist" aria-label={t('marketing.typeAria')}>
      {TIKTOK_ACTIVITY_TYPE_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : tab.value;
        const count = Number(typeCounts?.[countKey] ?? 0);
        const active = String(applied.activityType || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tko-status-tab${active ? ' tko-status-tab--active' : ''}`}
            onClick={() => onActivityTypeTab(tab.value)}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            <span className="tko-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [applied.activityType, typeCounts, t]);

  const statusTabs = useMemo(() => (
    <div className="tko-status-tabs" role="tablist" aria-label={t('marketing.statusAria')}>
      {TIKTOK_ACTIVITY_STATUS_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : tab.value;
        const count = Number(statusCounts?.[countKey] ?? 0);
        const active = String(applied.status || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all-status'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tko-status-tab${active ? ' tko-status-tab--active' : ''}`}
            onClick={() => onStatusTab(tab.value)}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            <span className="tko-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [applied.status, statusCounts, t]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(
      shopDimensionTabs,
      pageView === 'activities' ? (
        <>
          {typeTabs}
          {statusTabs}
        </>
      ) : null,
    ),
    [shopDimensionTabs, pageView, statusTabs, typeTabs],
  );

  const renderCheckTh = () => (
    <th
      scope="col"
      className="tko-ops-th tko-ops-th--check tiktok-product-cell--spu-check"
      onClick={() => {
        if (!shopId || loading || selectableKeys.length === 0) return;
        toggleAll();
      }}
    >
      <input
        type="checkbox"
        aria-label={t(meta.selectAllLabelKey)}
        disabled={!shopId || loading || selectableKeys.length === 0}
        checked={allSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={toggleAll}
      />
    </th>
  );

  const renderCheckTd = (key, row, rowSpan) => (
    <td
      rowSpan={rowSpan}
      className="tko-ops-td tko-ops-td--check tiktok-product-cell tiktok-product-cell--spu-check"
      onClick={() => toggleOne(key, row)}
    >
      <input
        type="checkbox"
        aria-label={fixedUiTpl('选择 {0}', key)}
        checked={selectedKeys.has(key)}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggleOne(key, row)}
      />
    </td>
  );

  const renderEnrollRows = () => rows.flatMap((row, index) => {
    const key = rowKey(row, pageView, index);
    const selected = selectedKeys.has(key);
    const skcItems = enrollSkcItems(row);
    const rowSpan = Math.max(1, skcItems.length);
    const sessions = Array.isArray(row.sessions) ? row.sessions : [];
    const activityLabel = row.activityThematicName || row.name || '—';
    const productTitle = row.title || row.productName || fixedUi('未命名货品');
    const productSupplierCode = enrollProductSupplierCode(row, skcItems);
    const previewSessions = sessions.slice(0, 3);
    const activityStatusLabel = tiktokActivityStatusLabel(
      row.activityStatus ?? row.sessionStatus ?? row.activityStatusLabel ?? row.sessionStatusLabel ?? row.status,
    );
    const activityStatusCode = row.activityStatus ?? row.sessionStatus;
    const enrollStatusLabel = row.enrollStatusLabel || '—';
    const soldTone = soldStatusTone(row.soldStatus);
    const soldLabel = fixedUi(row.soldStatusLabel || '正常');

    return skcItems.map((skc, skcIdx) => {
      const isFirst = skcIdx === 0;
      const currency = skc.currency || row.currency;
      const activityDiscount = formatActivityDiscount(skc.activityPrice, skc.supplyPrice);
      return (
        <tr
          key={`${key}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
          className={`tiktok-product-row tiktok-draft-row tko-row--clickable${isFirst ? '' : ' tiktok-draft-row--skc-child'}${selected ? ' is-selected' : ''}`}
          onClick={(e) => onRowClickSelect(e, key, row)}
        >
          {isFirst ? (
            <>
              {renderCheckTd(key, row, rowSpan)}
              <td rowSpan={rowSpan} className="tiktok-product-cell tiktok-product-cell--info">
                <div className="tiktok-product-info tiktok-product-info--text-only tiktok-draft-product-info">
                  <div className="tiktok-product-info__text">
                    <HoverCopyField
                      value={productTitle}
                      className="tiktok-product-info__title"
                      popoverCopy
                    />
                    <HoverCopyField
                      label={fixedUi("产品ID")}
                      value={row.productId != null ? String(row.productId) : ''}
                      className="tiktok-product-info__meta"
                      separator=": "
                    />
                    <HoverCopyField
                      label={fixedUi("货号")}
                      value={productSupplierCode}
                      className="tiktok-product-info__meta"
                      separator=": "
                      emptyText="—"
                    />
                  </div>
                </div>
              </td>
            </>
          ) : null}
          <td className="tiktok-product-cell tiktok-product-cell--spec">
            <div className="tiktok-product-spec tiktok-draft-skc">
              <TiktokProductThumb imageUrl={skc.imageUrl || row.productImageUrl} alt={skc.colorLabel || fixedUi("主规格")} />
              <div className="tiktok-product-spec__text">
                <div className="tiktok-product-spec__color">
                  {skc.colorLabel || '—'}
                </div>
              </div>
            </div>
          </td>
          <td className="tiktok-product-cell tiktok-product-cell--price tiktok-product-cell--supply-price">
            {formatMoney(skc.supplyPrice, currency)}
          </td>
          <td className="tiktok-product-cell tiktok-product-cell--price tiktok-product-cell--activity-price">
            <div className="tko-marketing-enroll-activity-price">
              <div className="tko-marketing-enroll-activity-price__value">
                {formatMoney(skc.activityPrice, currency)}
              </div>
              {activityDiscount ? (
                <div
                  className={`tko-marketing-enroll-activity-price__discount${activityDiscount === fixedUi('原价') ? ' tko-marketing-enroll-activity-price__discount--full' : ''}`}
                >
                  {activityDiscount}
                </div>
              ) : null}
            </div>
          </td>
          {isFirst ? (
            <>
              <td rowSpan={rowSpan} className="tiktok-product-cell tiktok-product-cell--enroll-info">
                <div className="tko-marketing-enroll-info">
                  <div>
                    <span className="tko-marketing-enroll-info__label">{fixedUi("报名时间：")}</span>
                    <span>{row.enrollTime || '—'}</span>
                  </div>
                  <div>
                    <span className="tko-marketing-enroll-info__label">{fixedUi("活动开始时间：")}</span>
                    <span>{row.sessionStartTime || row.startTime || '—'}</span>
                  </div>
                  <div>
                    <span className="tko-marketing-enroll-info__label">{fixedUi("活动结束时间：")}</span>
                    <span>{row.sessionEndTime || row.endTime || '—'}</span>
                  </div>
                  <div>
                    <span className="tko-marketing-enroll-info__label">{fixedUi("活动名称：")}</span>
                    <span>{activityLabel}</span>
                  </div>
                </div>
              </td>
              <td rowSpan={rowSpan} className="tiktok-product-cell tiktok-product-cell--activity-status">
                <span className={`tko-records__status tko-records__status--${activityStatusTone(activityStatusCode ?? activityStatusLabel)}`}>
                  {activityStatusLabel}
                </span>
              </td>
              <td rowSpan={rowSpan} className="tiktok-product-cell tiktok-product-cell--sessions">
                {sessions.length ? (
                  <div className="tko-marketing-enroll-sessions">
                    {previewSessions.map((session, sIdx) => {
                      const statusText = session.enrollStatusLabel || enrollStatusLabel;
                      const statusTone = enrollStatusTone(session.enrollStatus ?? row.enrollStatus ?? statusText);
                      return (
                        <div
                          key={session.sessionId || sIdx}
                          className="tko-marketing-enroll-session"
                        >
                          <span>{session.siteName || '—'}</span>
                          <span>{session.timeLabel || [session.startTime, session.endTime].filter(Boolean).join(' - ') || '—'}</span>
                          <span className={`tko-marketing-enroll-session__status tko-marketing-enroll-session__status--${statusTone}`}>
                            {statusText}
                          </span>
                        </div>
                      );
                    })}
                    {sessions.length > previewSessions.length ? (
                      <button
                        type="button"
                        className="tko-marketing-enroll-sessions__more"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSessionsModal({
                            title: [
                              row.productId != null && row.productId !== ''
                                ? fixedUiTpl('产品ID: {0}', row.productId)
                                : null,
                              activityLabel !== '—' ? activityLabel : null,
                            ].filter(Boolean).join(' · ') || fixedUi("活动场次"),
                            sessions,
                            enrollStatusLabel,
                          });
                        }}
                      >
                        {fixedUi('更多')}({sessions.length})
                      </button>
                    ) : null}
                  </div>
                ) : '—'}
              </td>
              <td rowSpan={rowSpan} className="tiktok-product-cell tiktok-product-cell--stock">
                <div>{fixedUi('提报:')} {row.activityStock != null ? row.activityStock : '—'}</div>
                <div>{fixedUi('剩余:')} {row.remainingActivityStock != null ? row.remainingActivityStock : '—'}</div>
                <div className={`tko-marketing-sold-status tko-marketing-sold-status--${soldTone}`}>
                  {soldLabel}
                </div>
              </td>
            </>
          ) : null}
        </tr>
      );
    });
  });

  return (
    <>
      <TiktokSyncProgressSlot kinds={meta.syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || syncing || exporting}
            syncKinds={meta.syncKinds}
            quotaKind={meta.quotaKind}
            onSync={onSync}
            syncing={syncing || loading}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing || loading}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={t(meta.syncHintKey)}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(resolveSelectedRows()),
            })}
            batchOpsMenu={batchOpsMenu}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--w300">
              <span className="tko-field__label">{fixedUi("关键词")}</span>
              <input
                className="tko-input"
                type="search"
                value={filterDraft.keyword}
                disabled={!shopId}
                placeholder={t(meta.keywordPlaceholderKey)}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, keyword: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            {pageView === 'activities' ? (
              <div className="tko-field tko-field--inline tko-field--status">
                <span className="tko-field__label">{fixedUi("状态")}</span>
                <select
                  className="tko-select tko-select--status tko-select--compact"
                  value={filterDraft.status}
                  disabled={!shopId}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, status: e.target.value }))}
                >
                  {TIKTOK_ACTIVITY_STATUS_TABS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : fixedUi(opt.label)}</option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" disabled={!shopId} onClick={onReset}>
              {fixedUi("重置")}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!shopId || loading || neverSynced}
              onClick={onQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {error ? <div className="tko-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t(meta.loadingLabelKey)}
      >
        {pageView === 'enrolls' ? (
          <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
            <div className="wf-tasks-table-panel">
              <div className="wf-tasks-table-wrap tko-ops-table-wrap" role="region" aria-live="polite">
                <table className="wf-tasks-table platform-product-table platform-product-table--tiktok platform-product-table--drafts platform-product-table--records platform-product-table--marketing-enrolls tko-ops-table--sticky-head">
                  <colgroup>
                    <col className="tiktok-product-col--check" />
                    <col className="tiktok-product-col--info" />
                    <col className="tiktok-product-col--skc" />
                    <col className="tiktok-product-col--price tiktok-product-col--supply-price" />
                    <col className="tiktok-product-col--price tiktok-product-col--activity-price" />
                    <col className="tiktok-product-col--enroll-info" />
                    <col className="tiktok-product-col--activity-status" />
                    <col className="tiktok-product-col--sessions" />
                    <col className="tiktok-product-col--stock" />
                  </colgroup>
                  <thead>
                    <tr>
                      {renderCheckTh()}
                      <th scope="col">{fixedUi("产品信息")}</th>
                      <th scope="col">{fixedUi("主规格")}</th>
                      <th scope="col" className="tiktok-product-th--supply-price">{fixedUi("供货价")}</th>
                      <th scope="col" className="tiktok-product-th--activity-price">{fixedUi("活动价")}</th>
                      <th scope="col">{fixedUi("报名信息")}</th>
                      <th scope="col" className="tiktok-product-th--activity-status">{fixedUi("活动状态")}</th>
                      <th scope="col">{fixedUi("活动场次")}</th>
                      <th scope="col" className="tiktok-product-th--stock">{fixedUi("提报/剩余")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!shopId || loading || rows.length === 0 ? (
                      <tr>
                        <td colSpan={meta.colSpan} className="wf-tasks-table__empty">
                          <div className="funding-empty-state">
                            <p className="funding-empty-state__title">{emptyTitle}</p>
                            {emptyHint ? (
                              <p className="funding-empty-state__description">{emptyHint}</p>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      renderEnrollRows()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="tko-drafts__table-wrap tko-ops-table-wrap">
            <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--sticky-head">
              <colgroup>
                <col className="tko-ops-col tko-ops-col--check tiktok-product-col--check" />
              </colgroup>
              <thead>
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{fixedUi("活动名称")}</th>
                  <th scope="col">{fixedUi("类型")}</th>
                  <th scope="col">{fixedUi("状态")}</th>
                  <th scope="col">{fixedUi("报名截止")}</th>
                  <th scope="col">{fixedUi("活动起止")}</th>
                  <th scope="col">{fixedUi("活动 ID")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {!shopId || loading || rows.length === 0 ? (
                  <tr>
                    <td colSpan={meta.colSpan} className="tko-certs-empty-cell">
                      <div className="tko-certs-empty">
                        <p className="tko-certs-empty__title">{emptyTitle}</p>
                        {emptyHint ? <p className="tko-certs-empty__hint">{emptyHint}</p> : null}
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const key = rowKey(row, pageView, index);
                    const selected = selectedKeys.has(key);
                    return (
                      <tr
                        key={key}
                        className={`tko-row--clickable${selected ? ' is-selected' : ''}`}
                        onClick={(e) => onRowClickSelect(e, key, row)}
                      >
                        {renderCheckTd(key, row)}
                        <td>{row.name || row.activityName || row.title || '—'}</td>
                        <td>{activityTypeDisplayLabel(row)}</td>
                        <td>
                          <span className={`tko-records__status tko-records__status--${tiktokActivityStatusTone(row.status || row.activityStatus)}`}>
                            {activityStatusDisplayLabel(row)}
                          </span>
                        </td>
                        <td>{row.enrollDeadline || '—'}</td>
                        <td>
                          {[row.startTime, row.endTime].filter(Boolean).join(' ~ ') || '—'}
                        </td>
                        <td>{row.activityId || row.id || '—'}</td>
                        <td>
                          <div className="tko-marketing-list__ops">
                            <button
                              type="button"
                              className="tko-btn tko-btn--ghost tko-btn--sm"
                              onClick={() => {
                                setDrawerTab('detail');
                                setDrawerActivity(row);
                              }}
                            >
                              {fixedUi("查看")}
                            </button>
                            <button
                              type="button"
                              className="tko-btn tko-btn--primary tko-btn--sm"
                              onClick={() => {
                                const activityId = row.activityId || row.id;
                                if (!shopId || !activityId) {
                                  showToast(fixedUi("请先选择店铺"), 'warning');
                                  return;
                                }
                                const q = new URLSearchParams();
                                q.set('shopId', String(shopId));
                                const name = row.name || row.activityName || row.title;
                                if (name) q.set('name', String(name));
                                if (row.activityType != null) q.set('activityType', String(row.activityType));
                                const level = row.product_level || row.productLevel;
                                if (level) q.set('productLevel', String(level));
                                openTiktokPathInNewTab(buildTiktokPath(`marketing/enroll/${encodeURIComponent(activityId)}?${q.toString()}`));
                              }}
                            >
                              {fixedUi("提报商品")}
                            </button>
                            <button
                              type="button"
                              className="tko-btn tko-btn--ghost tko-btn--sm"
                              disabled={Boolean(row.immutable)}
                              title={row.immutable ? fixedUi("平台标记为不可修改") : undefined}
                              onClick={() => {
                                setEditActivity(row);
                                setEditForm({
                                  title: row.name || row.activityName || row.title || '',
                                  begin_time: epochToDateInput(row.begin_time),
                                  end_time: epochToDateInput(row.end_time),
                                });
                              }}
                            >
                              {fixedUi("编辑")}
                            </button>
                            <button
                              type="button"
                              className="tko-btn tko-btn--ghost tko-btn--sm"
                              onClick={() => {
                                setRepublishActivity(row);
                                setRepublishForm({
                                  begin_time: epochToDateInput(row.begin_time),
                                  end_time: epochToDateInput(row.end_time),
                                });
                              }}
                            >
                              {fixedUi("重新发布")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t(meta.pagerLabelKey)}
        />
      </ProductListCard>

      {pageView === 'activities' ? (
        <TiktokMarketingActivityDrawer
          open={Boolean(drawerActivity)}
          shopId={shopId}
          activity={drawerActivity}
          initialTab={drawerTab}
          onClose={() => setDrawerActivity(null)}
        />
      ) : null}

      {createOpen ? (
        <div className="tko-batch-modal" role="dialog" aria-modal="true">
          <button type="button" className="tko-batch-modal__backdrop" aria-label={fixedUi("关闭")} onClick={() => setCreateOpen(false)} />
          <div className="tko-batch-modal__panel">
            <header className="tko-batch-modal__head">
              <h2>{fixedUi("创建促销活动")}</h2>
              <button type="button" className="tko-batch-modal__close" onClick={() => setCreateOpen(false)}>×</button>
            </header>
            <form
              className="tko-batch-modal__body"
              style={{ display: 'grid', gap: 10 }}
              onSubmit={async (event) => {
                event.preventDefault();
                if (!shopId) return;
                const begin = dayStartEpochSeconds(createForm.begin_time);
                const end = dayEndEpochSeconds(createForm.end_time);
                if (!createForm.title.trim() || !begin || !end) {
                  showToast(fixedUi("请填写名称和起止日期"), 'warning');
                  return;
                }
                setCreateBusy(true);
                try {
                  const payload = {
                    title: createForm.title.trim(),
                    activity_type: createForm.activity_type,
                    begin_time: begin,
                    end_time: end,
                    product_level: createForm.activity_type === 'SHIPPING_DISCOUNT'
                      ? 'SHOP'
                      : (createForm.activity_type === 'FIXED_PRICE'
                        || createForm.activity_type === 'DIRECT_DISCOUNT'
                        || createForm.activity_type === 'FLASHSALE')
                        ? 'VARIATION'
                        : 'PRODUCT',
                  };
                  if (createForm.activity_type === 'SHIPPING_DISCOUNT') {
                    payload.discount = {
                      shipping_discount: {
                        threshold_type: 'NO_THRESHOLD',
                        type: 'FREE_SHIPPING',
                        area_scope: { type: 'WHOLE' },
                      },
                    };
                  }
                  if (createForm.activity_type === 'BUY_MORE_SAVE_MORE') {
                    payload.discount = {
                      bmsm_discount: {
                        details: [{
                          tier: 1,
                          threshold_type: 'MINIMAL_ITEM_QUANTITY',
                          threshold_value: '2',
                          type: 'PERCENTAGE_OFF',
                          value: '10',
                        }],
                      },
                    };
                  }
                  if (createForm.activity_type === 'BUY_X_GET_Y') {
                    payload.discount = {
                      bxgy_discount: {
                        threshold_type: 'MINIMAL_ITEM_QUANTITY',
                        threshold_value: '2',
                        discount_type: 'PERCENTAGE_OFF',
                        discount_value: '10',
                        benefit_max_applicable_quantity: '1',
                      },
                    };
                  }
                  await tiktokPlatformAPI.createPromotionActivity(shopId, payload);
                  showToast(fixedUi("活动已创建，请刷新列表"), 'success');
                  setCreateOpen(false);
                } catch (e) {
                  showToast(e?.message || fixedUi('创建失败'), 'error');
                } finally {
                  setCreateBusy(false);
                }
              }}
            >
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("名称")}</span>
                <input className="tko-input" value={createForm.title} onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))} />
              </label>
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("类型")}</span>
                <select className="tko-input" value={createForm.activity_type} onChange={(e) => setCreateForm((prev) => ({ ...prev, activity_type: e.target.value }))}>
                  <option value="DIRECT_DISCOUNT">{fixedUi("直接折扣")}</option>
                  <option value="FIXED_PRICE">{fixedUi("固定价格")}</option>
                  <option value="FLASHSALE">{fixedUi("秒杀")}</option>
                  <option value="SHIPPING_DISCOUNT">{fixedUi("运费折扣")}</option>
                  <option value="BUY_MORE_SAVE_MORE">{fixedUi("满减")}</option>
                  <option value="BUY_X_GET_Y">{fixedUi("买 X 送 Y")}</option>
                </select>
              </label>
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("开始日期")}</span>
                <input className="tko-input" type="date" value={createForm.begin_time} onChange={(e) => setCreateForm((prev) => ({ ...prev, begin_time: e.target.value }))} />
              </label>
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("结束日期")}</span>
                <input className="tko-input" type="date" value={createForm.end_time} onChange={(e) => setCreateForm((prev) => ({ ...prev, end_time: e.target.value }))} />
              </label>
              <footer className="tko-batch-modal__foot">
                <button type="button" className="tko-btn tko-btn--secondary" onClick={() => setCreateOpen(false)}>{fixedUi("取消")}</button>
                <button type="submit" className="tko-btn tko-btn--primary" disabled={createBusy}>{createBusy ? fixedUi("创建中…") : fixedUi("创建")}</button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {editActivity ? (
        <div className="tko-batch-modal" role="dialog" aria-modal="true">
          <button type="button" className="tko-batch-modal__backdrop" aria-label={fixedUi("关闭")} onClick={() => setEditActivity(null)} />
          <div className="tko-batch-modal__panel">
            <header className="tko-batch-modal__head">
              <h2>{fixedUi("更新活动")}</h2>
              <button type="button" className="tko-batch-modal__close" onClick={() => setEditActivity(null)}>×</button>
            </header>
            <form
              className="tko-batch-modal__body"
              style={{ display: 'grid', gap: 10 }}
              onSubmit={async (event) => {
                event.preventDefault();
                const activityId = editActivity.activityId || editActivity.id;
                const begin = dayStartEpochSeconds(editForm.begin_time);
                const end = dayEndEpochSeconds(editForm.end_time);
                if (!shopId || !activityId || !editForm.title.trim() || !begin || !end) {
                  showToast(fixedUi("请填写名称和起止日期"), 'warning');
                  return;
                }
                setEditBusy(true);
                try {
                  const payload = {
                    title: editForm.title.trim(),
                    begin_time: begin,
                    end_time: end,
                    product_level: editActivity.product_level || editActivity.productLevel || 'PRODUCT',
                  };
                  if (editActivity.discount && typeof editActivity.discount === 'object') {
                    payload.discount = editActivity.discount;
                  }
                  await tiktokPlatformAPI.updatePromotionActivity(shopId, activityId, payload);
                  showToast(fixedUi("活动已更新"), 'success');
                  setEditActivity(null);
                } catch (e) {
                  showToast(e?.message || fixedUi('更新活动失败'), 'error');
                } finally {
                  setEditBusy(false);
                }
              }}
            >
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("名称")}</span>
                <input className="tko-input" value={editForm.title} onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))} />
              </label>
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("开始日期")}</span>
                <input className="tko-input" type="date" value={editForm.begin_time} onChange={(e) => setEditForm((prev) => ({ ...prev, begin_time: e.target.value }))} />
              </label>
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("结束日期")}</span>
                <input className="tko-input" type="date" value={editForm.end_time} onChange={(e) => setEditForm((prev) => ({ ...prev, end_time: e.target.value }))} />
              </label>
              <footer className="tko-batch-modal__foot">
                <button type="button" className="tko-btn tko-btn--secondary" onClick={() => setEditActivity(null)}>{fixedUi("取消")}</button>
                <button type="submit" className="tko-btn tko-btn--primary" disabled={editBusy}>{editBusy ? fixedUi("保存中…") : fixedUi("保存")}</button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {republishActivity ? (
        <div className="tko-batch-modal" role="dialog" aria-modal="true">
          <button type="button" className="tko-batch-modal__backdrop" aria-label={fixedUi("关闭")} onClick={() => setRepublishActivity(null)} />
          <div className="tko-batch-modal__panel">
            <header className="tko-batch-modal__head">
              <h2>{fixedUi("重新发布活动")}</h2>
              <button type="button" className="tko-batch-modal__close" onClick={() => setRepublishActivity(null)}>×</button>
            </header>
            <form
              className="tko-batch-modal__body"
              style={{ display: 'grid', gap: 10 }}
              onSubmit={async (event) => {
                event.preventDefault();
                const activityId = republishActivity.activityId || republishActivity.id;
                const begin = dayStartEpochSeconds(republishForm.begin_time);
                const end = dayEndEpochSeconds(republishForm.end_time);
                if (!shopId || !activityId || !begin || !end) {
                  showToast(fixedUi("请填写新的起止日期"), 'warning');
                  return;
                }
                setRepublishBusy(true);
                try {
                  await tiktokPlatformAPI.republishPromotionActivity(shopId, activityId, {
                    begin_time: begin,
                    end_time: end,
                  });
                  showToast(fixedUi("活动已重新发布"), 'success');
                  setRepublishActivity(null);
                } catch (e) {
                  showToast(e?.message || fixedUi('重新发布失败'), 'error');
                } finally {
                  setRepublishBusy(false);
                }
              }}
            >
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("开始日期")}</span>
                <input className="tko-input" type="date" value={republishForm.begin_time} onChange={(e) => setRepublishForm((prev) => ({ ...prev, begin_time: e.target.value }))} />
              </label>
              <label className="tko-field">
                <span className="tko-field__label">{fixedUi("结束日期")}</span>
                <input className="tko-input" type="date" value={republishForm.end_time} onChange={(e) => setRepublishForm((prev) => ({ ...prev, end_time: e.target.value }))} />
              </label>
              <footer className="tko-batch-modal__foot">
                <button type="button" className="tko-btn tko-btn--secondary" onClick={() => setRepublishActivity(null)}>{fixedUi("取消")}</button>
                <button type="submit" className="tko-btn tko-btn--primary" disabled={republishBusy}>{republishBusy ? fixedUi("提交中…") : fixedUi("重新发布")}</button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <MarketingEnrollSessionsModal
        open={Boolean(sessionsModal)}
        title={sessionsModal?.title || fixedUi("活动场次")}
        sessions={sessionsModal?.sessions || []}
        enrollStatusLabel={sessionsModal?.enrollStatusLabel || ''}
        onClose={() => setSessionsModal(null)}
      />
    </>
  );
}
